import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, X } from "lucide-react";
import { RestaurantBrand } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { SearchBar } from "@/components/SearchBar";
import { CategoryNav } from "@/components/CategoryNav";
import { ProductCard } from "@/components/ProductCard";
import { NoResults } from "@/components/EmptyStates";
import { useLang } from "@/context/LanguageContext";
import { useUI } from "@/context/UIContext";
import { categories, products } from "@/data/menu";
import { DIET_FILTERS, QUICK_PICKS, productHasTag } from "@/data/productMeta";
import { restaurant } from "@/data/restaurant";
import { localize } from "@/lib/format";
import { searchProducts } from "@/lib/search";

export default function MenuPage() {
  const [params, setParams] = useSearchParams();
  const { lang, t } = useLang();
  const { openServices } = useUI();
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(params.get("cat") || "all");
  const [activeTag, setActiveTag] = useState(params.get("tag") || null);
  const [diets, setDiets] = useState([]);

  useEffect(() => {
    setActiveCat(params.get("cat") || "all");
    setActiveTag(params.get("tag") || null);
  }, [params]);

  const handleCat = (catId) => {
    setActiveCat(catId);
    const next = {};
    if (catId !== "all") next.cat = catId;
    if (activeTag) next.tag = activeTag;
    setParams(next, { replace: true });
  };

  const toggleDiet = (id) => setDiets((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
  const clearTag = () => { setActiveTag(null); const next = {}; if (activeCat !== "all") next.cat = activeCat; setParams(next, { replace: true }); };

  const catNameFor = (p) => localize(categories.find((c) => c.id === p.categoryId)?.name, lang);
  const activeTagLabel = useMemo(() => {
    const qp = QUICK_PICKS.find((q) => q.id === activeTag);
    return qp ? t(qp.key) : activeTag;
  }, [activeTag, t]);

  // Base filter (category + tag + dietary)
  const base = useMemo(() => {
    return products.filter((p) => {
      if (activeCat !== "all" && p.categoryId !== activeCat) return false;
      if (activeTag && !productHasTag(p, activeTag)) return false;
      if (diets.length && !diets.every((d) => productHasTag(p, d))) return false;
      return true;
    });
  }, [activeCat, activeTag, diets]);

  // Fuzzy search within base
  const { results, suggestions } = useMemo(() => {
    if (!query.trim()) return { results: base, suggestions: [] };
    return searchProducts(query, base, catNameFor, lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, base, lang]);

  const grouped = useMemo(() => {
    if (activeCat !== "all" || query.trim() || activeTag || diets.length) return null;
    return categories
      .map((c) => ({ cat: c, items: results.filter((p) => p.categoryId === c.id) }))
      .filter((g) => g.items.length);
  }, [results, activeCat, query, activeTag, diets]);

  return (
    <div data-testid="menu-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 glass pt-safe">
        <div className="flex items-center justify-between px-4 py-3">
          <RestaurantBrand name={restaurant.name} branch={restaurant.branch.name} logoImage={restaurant.logoImage} />
          <div className="flex items-center gap-2">
            <button onClick={openServices} className="grid h-10 w-10 place-items-center rounded-full border border-border bg-card shadow-soft active:scale-95" aria-label={t("services")} data-testid="services-btn">
              <LayoutGrid className="h-5 w-5 text-primary" />
            </button>
            <LanguageSelector />
            <ThemeToggle />
          </div>
        </div>
        <div className="px-4 pb-3">
          <SearchBar value={query} onChange={setQuery} />
        </div>
        <CategoryNav active={activeCat} onSelect={handleCat} />
        <div className="no-scrollbar flex items-center gap-2 overflow-x-auto px-4 py-2.5">
          {activeTag && (
            <button onClick={clearTag} className="flex shrink-0 items-center gap-1.5 rounded-full border border-primary bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary" data-testid="active-tag-chip">
              {activeTagLabel} <X className="h-3.5 w-3.5" />
            </button>
          )}
          {DIET_FILTERS.map((d) => (
            <button
              key={d.id}
              onClick={() => toggleDiet(d.id)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors active:scale-95 ${
                diets.includes(d.id) ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground"
              }`}
              data-testid={`filter-${d.id}`}
            >
              {t(d.key)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-4 py-4">
        {results.length === 0 && suggestions.length === 0 ? (
          <NoResults />
        ) : results.length === 0 && suggestions.length > 0 ? (
          <div data-testid="did-you-mean">
            <h2 className="mb-3 font-heading text-lg font-extrabold">{t("didYouMean")}</h2>
            <div className="space-y-3">
              {suggestions.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        ) : grouped ? (
          <div className="space-y-7">
            {grouped.map(({ cat, items }) => (
              <section key={cat.id} data-testid={`menu-section-${cat.slug}`}>
                <h2 className="mb-3 font-heading text-lg font-extrabold">{localize(cat.name, lang)}</h2>
                <div className="space-y-3">
                  {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <motion.div layout className="space-y-3">
            {results.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </motion.div>
        )}
      </div>
    </div>
  );
}
